import React, { useState, useEffect } from "react";
import { ThemeProvider } from "@mui/material/styles";
import { ARROW_RIGHT } from "Components/Icons";
import { CircularProgress } from "Components/Base/Feedback/CircularProgress";
import format from "lib/format";
import config from "lib/config";
import theme from "lib/themes/theme";
import withLanguageContext from "lib/hocs/withLanguageContext";
import { useMyLearning } from "lib/components/MyLearning/context";
import MyLearningProvider from "lib/components/MyLearning/context/provider";
import CourseDetails from "lib/components/MyLearning/components/CourseDetails";
import ProgramDetails from "lib/components/MyLearning/components/programs/Details";
import MainContent from "lib/components/MyLearning/components/MainView";
import { HeadingPanel } from "lib/components/MyLearning/components/Headings";
import Filter from "lib/components/MyLearning/components/Filter";
import { MODE_SIMPLE } from "lib/components/MyLearning/constants";
import UserStore from "stores/user";
import SettingStore from "stores/settings";

const ShowAllLink = ({ label }) => (
  <div className={"flex flex-end cardview-showall align-c"}>
    <a href={format.url("/#/tm/coworker/mycourses")}>{label}</a>
    <ARROW_RIGHT />
  </div>
);

const isBrowser = typeof window !== "undefined";
const hasLuvit = isBrowser && window.luvit;
const hasGetQueryString =
  hasLuvit && typeof window.luvit.GetQueryString === "function";
const queryPersonalId = hasGetQueryString
  ? window.luvit.GetQueryString("id")
  : null;

const initialCoursePanelState = {
  ready: false,
  courseDetails: null,
  personalId: queryPersonalId || config.personalId,
  managerView: false,
  programDetails: false,
  programActivityId: null,
};

const MyCoursesPanel = ({ languageContext }) => {
  const [state, setState] = useState(initialCoursePanelState);

  const {
    getMyLearningData,
    courses,
    searching,
    isBt,
    viewLimit,
    setViewLimit,
    setViewLimitNr,
  } = useMyLearning();

  useEffect(() => {
    const init = async () => {
      setState((prev) => ({ ...prev, ready: false }));
      await getMyLearningData(state.personalId);
      setState((prev) => ({ ...prev, ready: true }));
      await SettingStore.init();
      const viewLimitNr = SettingStore.getSettingValue(
        "noofcoursesinmycoursespanel",
      );
      setViewLimitNr(viewLimitNr);
    };

    init();
  }, []);

  useEffect(() => {
    if (state.ready) {
      setViewLimit(true);
    }
  }, [state.ready]);

  const openDetails = (course) => {
    setState((prev) => ({ ...prev, courseDetails: course }));
  };

  const toggleProgramDetails = (activityId) => {
    setState((prev) => ({
      ...prev,
      programDetails: !prev.programDetails,
      programActivityId: activityId,
    }));
  };

  const closeDetails = () => {
    setState((prev) => ({ ...prev, courseDetails: null }));
  };

  if (!state.ready) return <CircularProgress />;
  const isManagerOrAdmin = state.managerView || UserStore.user.fromAdmin;
  const displayFilter = courses.length > 0 && !isBt;
  const displayShowAllLink = viewLimit && !isBt;
  return (
    <ThemeProvider theme={theme}>
      <>
        {!state.programDetails && (
          <div className="bgColor-6 padding-10" style={{ maxWidth: "1050px" }}>
            <HeadingPanel
              title={
                isBt
                  ? languageContext("programsandprogress")
                  : languageContext("mylearning")
              }
            />
            {displayFilter && (
              <Filter mode={MODE_SIMPLE} languageContext={languageContext} />
            )}
            {searching ? (
              <CircularProgress />
            ) : (
              <MainContent
                isManagerOrAdmin={isManagerOrAdmin}
                onProgramClick={toggleProgramDetails}
                openDetails={openDetails}
                closeDetails={closeDetails}
                languageContext={languageContext}
              />
            )}

            {displayShowAllLink && (
              <ShowAllLink label={languageContext("showall")} />
            )}

            {state.courseDetails && (
              <CourseDetails
                initiallyOpen
                title={state.courseDetails.title}
                dialogStyles={{ minWidth: "450px" }}
                course={state.courseDetails}
                closeDetails={closeDetails}
                personalId={state.personalId}
                isManagerOrAdmin={isManagerOrAdmin}
              />
            )}
          </div>
        )}

        {state.programDetails && (
          <ProgramDetails
            programActivityId={state.programActivityId}
            isManagerOrAdmin={isManagerOrAdmin}
            personalId={state.personalId}
            languageContext={languageContext}
            onToggle={toggleProgramDetails}
          />
        )}
      </>
    </ThemeProvider>
  );
};

const MyCoursesPanelWithProvider = (props) => (
  <MyLearningProvider>
    <MyCoursesPanel {...props} />
  </MyLearningProvider>
);

export default withLanguageContext(
  MyCoursesPanelWithProvider,
  "ActivityCentre",
);
